import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Quill from "quill";
import "quill/dist/quill.snow.css";

/**
 * Thin React 19-safe wrapper around Quill 2.x.
 * react-quill is not maintained for React 19, so we drive the real
 * Quill instance ourselves via refs instead of using that wrapper.
 *
 * Props:
 *  - initialValue: HTML string used to seed the editor ONCE on mount.
 *                   Changing this prop later does NOT re-sync the editor
 *                   (Quill owns the DOM after that). If you need to load
 *                   content asynchronously, only render <QuillEditor /> once
 *                   the initialValue is ready (gate on your loading state).
 *  - onChange(html): called on every edit with the current editor HTML.
 *  - style: optional style for the outer wrapper div.
 */
const QuillEditor = ({ initialValue = "", onChange, style }) => {
  const wrapperRef = useRef(null);
  const quillRef = useRef(null);
  const onChangeRef = useRef(onChange);

  // keep latest onChange without re-mounting Quill
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (!wrapperRef.current) return;

    // Clear out any leftover DOM (e.g. from React 18 StrictMode's
    // mount -> unmount -> mount dev-only double invoke) before building
    // a fresh Quill instance, so we never end up with two toolbars.
    wrapperRef.current.innerHTML = "";

    const editorNode = document.createElement("div");
    editorNode.style.minHeight = "260px";
    editorNode.style.background = "#fff";
    wrapperRef.current.appendChild(editorNode);

    const quill = new Quill(editorNode, {
      theme: "snow",
      modules: {
        toolbar: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link"],
          ["clean"],
        ],
      },
    });

    if (initialValue) {
      quill.clipboard.dangerouslyPasteHTML(initialValue);
    }

    quill.on("text-change", () => {
      onChangeRef.current?.(quill.root.innerHTML);
    });

    quillRef.current = quill;

    return () => {
      quillRef.current = null;
      if (wrapperRef.current) {
        wrapperRef.current.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount once; see note above about initialValue

  return <div ref={wrapperRef} style={style} />;
};

QuillEditor.propTypes = {
  initialValue: PropTypes.string,
  onChange: PropTypes.func,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default QuillEditor;
